import { useEffect, useState, useContext } from 'react';
import { FormControlLabel, Switch } from '@mui/material';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import Box from './Box';
import Note from '../components/Note';
import EmptyState from './EmptyState';
import UnifiedModal from './UnifiedModal';
import { Context } from '../../../AuthContext';
import { useCategory } from '../../../CategoryContext';
import { toast } from 'react-toastify';
import SortByMenu from '../../../components/SortByMenu/SortByMenu';
import { getWorkbenchSortOptions } from '../../../utils/workbenchSortOptions';
import '../Workbench.css';

const label = { inputProps: { 'aria-label': 'Switch' } };

const Notes = ({ isVertical = false, verticalId }) => {
  const { user } = useContext(Context);
  const { verticals } = useCategory();
  const [notes, setNotes] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [recentlyArchivedIds, setRecentlyArchivedIds] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchNotes = async () => {
      if (!user?.uid) return;
      setIsLoading(true);
      try {
        const token = await user.getIdToken();
        const url =
          process.env.REACT_APP_FLASK_WEBSERVER +
          'get_notes' +
          (isVertical && verticalId ? `?vertical_id=${encodeURIComponent(verticalId)}` : '');
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        setNotes(data.data);
      } catch (error) {
        console.error('Error fetching notes:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchNotes();
  }, [user?.uid, isVertical, verticalId]);

  const handleDelete = async (id) => {
    setNotes(notes.filter((el) => el._id !== id));
    try {
      if (!user) return;
      const token = await user.getIdToken();
      await fetch(`${process.env.REACT_APP_FLASK_WEBSERVER}delete_note/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (error) {
      console.log('Failed to delete note:', error);
    }
  };

  const handleCopyToPersonal = async (note) => {
    try {
      if (!user) return;
      const token = await user.getIdToken();
      const payload = {
        title: note.title,
        details: note.details,
        isCompleted: false,
        associatedVerticalId: verticalId,
        tagColor: '#356AC3',
      };
      await fetch(`${process.env.REACT_APP_FLASK_WEBSERVER}add_note`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
    } catch (error) {
      console.error('Failed to copy note to personal workbench:', error);
    }
  };

  const handleOpenModal = (item = null) => {
    setEditItem(item);
    setOpenModal(true);
  };

  const handleSave = (updatedItem) => {
    setNotes((prev) =>
      editItem
        ? prev.map((item) => (item._id === editItem._id ? updatedItem : item))
        : [updatedItem, ...prev]
    );
    setOpenModal(false);
  };

  const toggleHandler = async (id) => {
    const itemToUpdate = notes.find((item) => item._id === id);
    const isCurrentlyCompleted = itemToUpdate.isCompleted;

    setNotes(
      notes.map((item) =>
        item._id === id ? { ...item, isCompleted: !isCurrentlyCompleted } : item
      )
    );

    if (!isCurrentlyCompleted) {
      setRecentlyArchivedIds((prev) => [...prev, id]);
      setTimeout(() => {
        setRecentlyArchivedIds((prev) => prev.filter((itemId) => itemId !== id));
      }, 3000);
    }

    try {
      if (!user) return;
      const token = await user.getIdToken();
      await fetch(`${process.env.REACT_APP_FLASK_WEBSERVER}edit_note?id=${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ isCompleted: !isCurrentlyCompleted }),
      });
    } catch (error) {
      console.error('Failed to toggle note:', error);
    }
  };

  const handleDragEnd = async (result) => {
    if (!canManage) return;
    if (!result.destination) return;

    const filterFn = (item) =>
      showArchived ? true : !item.isCompleted || recentlyArchivedIds.includes(item._id);

    const sourceItem = notes.filter(filterFn)[result.source.index];
    const destinationItem = notes.filter(filterFn)[result.destination.index];

    const updated = Array.from(notes);
    const sourceIdx = updated.findIndex((item) => item._id === sourceItem._id);
    const destIdx = updated.findIndex((item) => item._id === destinationItem._id);

    const [moved] = updated.splice(sourceIdx, 1);
    updated.splice(destIdx, 0, moved);

    setNotes(updated);

    try {
      if (!user) return;
      const token = await user.getIdToken();
      await fetch(
        `${process.env.REACT_APP_FLASK_WEBSERVER}update_order_dashboard?collection=notes`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(updated.map((item) => ({ id: item._id }))),
        }
      );
    } catch (error) {
      console.error('Failed to update notes order:', error);
    }
  };

  // Owner/editor-only management rights. The personal workbench is always the
  // user's own, so it's always allowed there; the vertical workbench requires
  // edit rights. Members (no edit rights) cannot sort, and the per-item menu
  // (the only way to edit/delete) is hidden from them.
  const canManage =
    !isVertical ||
    (verticals || []).some(
      (v) =>
        v.id === verticalId &&
        (v.status === 'Created' || /^(editor|advancededitor)$/i.test(v.permission || ''))
    );
  const canSort = canManage;
  const sortOptions = getWorkbenchSortOptions({ isVertical, type: 'note', verticals });

  // Apply a chosen sort, then persist the new order the same way a drag does.
  const handleSort = async (option) => {
    const sorted = [...notes].sort(option.comparator);
    setNotes(sorted);
    try {
      if (!user) return;
      const token = await user.getIdToken();
      const res = await fetch(
        `${process.env.REACT_APP_FLASK_WEBSERVER}update_order_dashboard?collection=notes`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(sorted.map((item) => ({ id: item._id }))),
        }
      );
      if (res.ok) toast.success(`Sorted by ${option.label}`, { className: 'toast', autoClose: 4000 });
      else toast.error('Unable to save sort order', { className: 'toast', autoClose: 4000 });
    } catch (error) {
      console.error('Failed to sort notes:', error);
      toast.error('Unable to save sort order', { className: 'toast', autoClose: 4000 });
    }
  };

  const filteredNotes = notes.filter((note) =>
    showArchived ? true : !note.isCompleted || recentlyArchivedIds.includes(note._id)
  );

  const count = showArchived ? notes.length : notes.filter((note) => !note.isCompleted).length;

  return (
    <div className="workbench-tasks">
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '10px',
          padding: '0 16px',
        }}
      >
        <div>
          {canSort && sortOptions.length > 0 && notes.length > 1 && (
            <SortByMenu sections={[{ options: sortOptions }]} onSelect={handleSort} />
          )}
        </div>
        <FormControlLabel
          control={
            <Switch
              {...label}
              checked={showArchived}
              onChange={() => setShowArchived((prev) => !prev)}
            />
          }
          label="SHOW ARCHIVED"
          labelPlacement="start"
        />
      </div>

      <UnifiedModal
        open={openModal}
        handleClose={() => setOpenModal(false)}
        item={editItem}
        type="note"
        onSave={handleSave}
        isVertical={isVertical}
        verticalId={verticalId}
      />

      <Box
        count={count}
        onAddHandler={() => handleOpenModal()}
        title="Notebook"
        icon="note_stack.svg"
        isLoading={isLoading}
        canAdd={canManage}
      >
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="notes">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef} style={{ height: '100%' }}>
                {filteredNotes.length > 0 ? (
                  filteredNotes.map((note, index) => (
                    <Draggable
                      key={note._id}
                      draggableId={note._id}
                      index={index}
                      isDragDisabled={!canManage}
                    >
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                        >
                          <Note
                            id={note._id}
                            deleteHandler={() => handleDelete(note._id)}
                            editHandler={() => handleOpenModal(note)}
                            toggleHandler={() => toggleHandler(note._id)}
                            title={note.title}
                            details={note.details}
                            isCompleted={note.isCompleted}
                            associatedVerticalId={isVertical ? null : note.associatedVerticalId}
                            tagColor={isVertical ? null : note.tagColor}
                            isVertical={isVertical}
                            assignedBy={note.assignedBy}
                            createdAt={note.created_at}
                            copyToPersonalHandler={
                              isVertical ? () => handleCopyToPersonal(note) : undefined
                            }
                            canManage={canManage}
                          />
                        </div>
                      )}
                    </Draggable>
                  ))
                ) : (
                  <EmptyState
                    imageSrc="/note_stack.svg"
                    type="note"
                    onAddClick={canManage ? () => handleOpenModal() : undefined}
                  />
                )}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </Box>
    </div>
  );
};

export default Notes;
